import React, { useContext, useState } from 'react'
import { Cell, CellIndent, Grid, Row } from '../fragments/comp/grid'
import { Line, Line2, Panel, PanelAction } from '../fragments/comp/panel'
import * as OC from '../data-comp'
import { GET_ORDER_PAYMENTS } from '../model-payments'
import { applyOrderAdjustments, applyOrderAdjustmentsHierarchy, CartCharge, Charge, GET_ORDER, Order, OrderCtx } from '../model-order'
import { useQuery } from '@apollo/react-hooks'
import { ApolloClient } from 'apollo-boost'
import { PaymentsAPI2ClientCtx, OrdersAPI5ClientCtx } from 'context'
import { useHistory, useRouteMatch } from 'react-router'

import { EditOrderAction, EditOrderAPI, EditOrderSvc, getActionDescription, useEditOrderSvc } from './service'
import Icon from 'react-fa'
import { AddItem } from './actions/add-item'
import AddModifierEditor from './actions/add-modifier'
import { ChangeItemQuantity } from './actions/change-item-quantity'
import { EditTip } from './actions/edit-tip'
import { EditDelivery } from './actions/edit-delivery'
import { AddCharge } from './actions/add-charge'
import { ChangeCharge } from './actions/change-charge'
import { EditServiceCharge } from './actions/edit-service'

import { ActionEditorKind, ActionEditor } from './model'
import { DetailedCharges } from './components/detailed-charges'
import { HItems } from './components/items'
import { EditOrderType } from './actions/order-type'
import { EditDiscount } from './actions/edit-discount'
import { EditDeliveryAddress } from './actions/edit-delivery-address'
import { EditOrderTime } from './actions/edit-time'

export function EditOrder(): JSX.Element {
  const match = useRouteMatch<{ orderId: string }>()
  const { orderId } = match.params

  const paymentsClient = useContext(PaymentsAPI2ClientCtx)
  const ordersClient = useContext(OrdersAPI5ClientCtx)
  if (!ordersClient || !paymentsClient) {
    return <>Loading order data...</>
  }

  const orderQuery = useQuery(GET_ORDER, {
    variables: { id: orderId },
    client: ordersClient,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })

  const paymentsQuery = useQuery(GET_ORDER_PAYMENTS, {
    variables: {
      order_id: orderId,
    },
    client: paymentsClient as ApolloClient<unknown>,
  })

  if (orderQuery.loading || paymentsQuery.loading) {
    return <div>Loading order version...</div>
  }

  if (orderQuery.error || paymentsQuery.error) {
    console.log('Errors:', orderQuery.error, paymentsQuery.error)
    return <>Error loading order and payments info</>
  }

  return (
    <EditOrderSvc order={applyOrderAdjustmentsHierarchy(orderQuery.data.order)} paymentOrder={paymentsQuery.data.order}>
      <EditOrderUI />
    </EditOrderSvc>
  )
}

function EditOrderUI(): JSX.Element {
  const match = useRouteMatch<{ orderId: string; paymentId: string }>()
  const { paymentId } = match.params
  const editOrderSvc = useEditOrderSvc()
  const [actionEditor, setActionEditor] = useState<{ action: ActionEditor, resolve: (action: EditOrderAction) => void, reject: () => void } | null>(null)
  const history = useHistory()
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)

  const openEditor = (action: ActionEditor): Promise<EditOrderAction> => {
    return new Promise<EditOrderAction>((resolve, reject) => {
      setActionEditor({ action, resolve, reject })
    })
  }
  const closeEditor = () => {
    if (actionEditor && actionEditor.reject) { actionEditor.reject() }
    setActionEditor(null)
  }

  const finishEditor = (action: EditOrderAction) => {
    editOrderSvc.recordChange(action)
    if (actionEditor) actionEditor.resolve(action)
  }

  const onSave = (): void => {
    if (busy) { return }
    setBusy(true)
    if (comment.split(/\s+/).length < 3) {
      alert('Please provide a reason for which this change has been made (min 3 words)')
      setBusy(false)
      return
    }

    if (editOrderSvc.getChangesCount() > 0) {
      editOrderSvc
        .commitToServer(parseInt(paymentId), comment)
        .then((res) => {
          setBusy(false)
          goBack()
        })
        .catch((err) => {
          console.log("Error", err)
          setBusy(false)
          alert(err.message)
        })
    } else {
      alert('There are no changes to be saved for this order')
    }
  }
  const goBack = (): void => {
    history.push(`/orders/${editOrderSvc.getOrderUUID()}`)
  }

  return (
    <>
      <OrderInfo key={`info-${editOrderSvc.getChangesCount()}`} openEditor={openEditor} />
      <OrderItems key={`items-${editOrderSvc.getChangesCount()}`} openEditor={openEditor} />
      <OrderTotals key={`totals-${editOrderSvc.getChangesCount()}`} openEditor={openEditor} />
      <ChangesSummary />
      <Panel
        actions={[
          { id: 'cancel', label: 'Cancel', onClick: goBack, style: 'cancel' },
          { id: 'finish', label: (busy ? 'Performing order updates....' : 'Save Changes & Perform Charges/Refunds'), onClick: onSave, style: 'finish' },
        ]}
        id={'finish'}
        label={`Edit Order: ${editOrderSvc.getChangesCount()} updates`}>
        <Line2>
          <textarea
            value={comment}
            onChange={(ev) => setComment(ev.target.value)}
            placeholder="Please provide a reason of this edit order change. (Required)"
            style={{ width: '100%', border: comment.split(/\s+/).length < 3 ? '1.5px dashed red' : '1.5px solid green' }} rows={3} />
        </Line2>
      </Panel>
      {actionEditor && renderActionEditor(editOrderSvc, actionEditor.action, finishEditor, closeEditor)}
    </>
  )
}

function OrderInfo(props: { openEditor: (actionEditor: ActionEditor) => Promise<EditOrderAction> }): JSX.Element {
  const data = useContext(OrderCtx) as Order
  const full_address = data.carts.settings.service.id === 'DELIVERY' ? data.carts.settings.service.address.full : '---'

  const EditServiceTypeButton = (): JSX.Element => {
    return (
      <div
        className="badge badge-warning"
        style={{ cursor: 'pointer' }}
        title={'Edit Service Type'}
        onClick={() => props.openEditor({ kind: ActionEditorKind.EditOrderType })}>
        <Icon name="edit" />
      </div>
    )
  }

  const EditDeliveryAddressButton = (): JSX.Element => {
    return (
      <div
        className="badge badge-warning"
        style={{ cursor: 'pointer' }}
        title={'Edit Delivery Address'}
        onClick={() => props.openEditor({ kind: ActionEditorKind.EditDeliveryAddress })}>
        <Icon name="edit" />
      </div>
    )
  }

  const EditOrderTimeButton = (): JSX.Element => {
    return (
      <div
        className="badge badge-warning"
        style={{ cursor: 'pointer' }}
        title={'Edit Order Time'}
        onClick={() => props.openEditor({ kind: ActionEditorKind.EditOrderTime })}>
        <Icon name="edit" />
      </div>
    )
  }

  return (
    <div className="row">
      <div className="col-12">
        <Panel id="order-items" label="Order Info">
          <Line label="Id">
            <span className="badge badge-success">
              <OC.ShortOrderUUID />
            </span>{' '}
            Conf Code:{' '}
            <span className="badge badge-success">
              <OC.ConfCode />
            </span>
          </Line>
          <Line label="Service">
            <div className="badge badge-success"><OC.Service.Label /></div>{' '}<EditServiceTypeButton />{' '}
            <OC.Service.IsDelivery>
              to{' '}
              <OC.Service.DeliveryAddress>
                <a
                  href={`https://maps.google.com?q=${encodeURIComponent(full_address)}`}
                  target="_blank"
                  rel="noreferrer">
                  <OC.Address.Full />
                </a>{' '}<EditDeliveryAddressButton />
              </OC.Service.DeliveryAddress>
            </OC.Service.IsDelivery>
          </Line>
          <Line label="Customer">
            <OC.CustomerName /> (
            <a href={`mailto:${encodeURIComponent(data.carts.customer.email)}`}>
              <OC.CustomerEmail />
            </a>
            ) (
            <a href={`tel:${encodeURIComponent(data.carts.customer.phone)}`}>
              <OC.CustomerPhone />
            </a>
            )
          </Line>
          <Line label="Restaurant">
            <OC.RestaurantName /> (
            <OC.Service.RestaurantAddress>
              <OC.Address.Full />
            </OC.Service.RestaurantAddress>
            )
          </Line>
          <Line label="Order Time">
            <div className="badge badge-success"><OC.OrderTime /></div>{' '}<EditOrderTimeButton />
          </Line>
          <OC.Service.IsDelivery>
            <Line label="Ready for pickup @ restaurant"><OC.PickupTime /></Line>
          </OC.Service.IsDelivery>
        </Panel>
      </div>
    </div>
  )
}

function renderActionEditor(
  editOrderSvc: EditOrderAPI,
  actionEditor: ActionEditor | null,
  onSave: (action: EditOrderAction) => void,
  onClose: () => void
): JSX.Element {
  if (!actionEditor) return <></>
  switch (actionEditor.kind) {
    case ActionEditorKind.AddItem:
      return <AddItem onClose={onClose} onSave={onSave} />
    case ActionEditorKind.AddModifier:
      return (
        <AddModifierEditor
          cartItemId={actionEditor.cartItemId}
          itemName={actionEditor.itemName}
          onClose={onClose}
          onSave={onSave}
        />
      )
    case ActionEditorKind.ChangeItemQuantity:
      return (
        <ChangeItemQuantity
          cartItemId={actionEditor.cartItemId}
          cartItem={actionEditor.cartItem}
          onClose={onClose}
          onSave={onSave} />
      )
    case ActionEditorKind.EditTip:
      return <EditTip onClose={onClose} onSave={onSave} />
    case ActionEditorKind.EditDelivery:
      return <EditDelivery onClose={onClose} onSave={onSave} />
    case ActionEditorKind.EditService:
      return <EditServiceCharge onClose={onClose} onSave={onSave} />
    case ActionEditorKind.AddCharge:
      return <AddCharge onClose={onClose} onSave={onSave} />
    case ActionEditorKind.EditCharge:
      return <ChangeCharge onClose={onClose} onSave={onSave} charge_id={actionEditor.chargeId} />
    case ActionEditorKind.EditOrderType:
      return <EditOrderType onClose={onClose} onSave={onSave} />
    case ActionEditorKind.EditDiscount:
      return <EditDiscount onClose={onClose} onSave={onSave} />
    case ActionEditorKind.EditDeliveryAddress:
      return <EditDeliveryAddress onClose={onClose} onSave={onSave} />
    case ActionEditorKind.EditOrderTime:
      return <EditOrderTime onClose={onClose} onSave={onSave} />
    default:
      return <></>
  }
}

function OrderItems(props: { openEditor: (actionEditor: ActionEditor) => Promise<EditOrderAction> }): JSX.Element {
  const editOrderSvc = useEditOrderSvc()

  const actions = [createAddAction(editOrderSvc, () => props.openEditor({ kind: ActionEditorKind.AddItem }))]

  const RemoveItemButton = (): JSX.Element => {
    const currentItem = useContext(OC.ItemsCtx)
    if (currentItem.adjustments && currentItem.adjustments.length) return <></>
    return (
      <div
        className="badge badge-danger"
        style={{ cursor: 'pointer' }}
        title={'Remove item'}
        onClick={() =>
          confirm(`Are you sure you want to remove ${currentItem.name} from the order?`) &&
            currentItem.action
            ? editOrderSvc.discardAction(currentItem.action)
            : editOrderSvc.recordChange({ op: 'remove-item', item_id: currentItem.id, item_name: currentItem.name })
        }>
        <Icon name="remove" />
      </div>
    )
  }

  const RemoveModifierOptionButton = (): JSX.Element => {
    let currentItem = useContext(OC.ItemsCtx)
    const currentModifier = useContext(OC.ModifiersCtx)
    const currentOption = useContext(OC.OptionsCtx)

    currentItem = editOrderSvc.getLastItemVersion(currentItem)

    if (currentItem.adjustments && currentItem.adjustments.length) return <></>
    return (
      <div
        className="badge badge-danger"
        style={{ cursor: 'pointer' }}
        title={'Remove modifier option'}
        onClick={() =>
          confirm(
            `Are you sure you want to remove ${currentItem.name}'s modifier ${currentOption.name} (${currentModifier.name}) from the order?`
          ) &&
            currentOption.action
            ? editOrderSvc.discardAction(currentOption.action)
            : editOrderSvc.recordChange({
              op: 'remove-customization',
              item_id: currentItem.id,
              customization_id: currentOption.id,
              customization_name: currentOption.name,
              modifier_name: currentModifier.name,
              item_name: currentItem.name,
            })
        }>
        <Icon name="remove" />
      </div>
    )
  }

  const AddItemModifierButton = (): JSX.Element => {
    const currentItem = useContext(OC.ItemsCtx)
    if (currentItem.adjustments && currentItem.adjustments.length > 0) return <></>
    if (currentItem.item_id) {
      return (
        <div
          className="badge badge-success"
          style={{ cursor: 'pointer' }}
          title={'Add modifiers'}
          onClick={() =>
            props.openEditor({
              kind: ActionEditorKind.AddModifier,
              cartItemId: currentItem.id,
              itemName: currentItem.name,
            })
          }>
          <Icon name="plus" /> Add Modifier
        </div>
      )
    } else {
      return <></>
    }
  }

  const ChangeItemQuantityButton = (): JSX.Element => {
    const editOrderSvc = useEditOrderSvc()
    const currentItem = useContext(OC.ItemsCtx)

    if (currentItem.adjustments && currentItem.adjustments.length) return <></>
    return (
      <div
        className="badge badge-warning"
        style={{ cursor: 'pointer' }}
        title={'Change quantity'}
        onClick={() =>
          props.openEditor({
            kind: ActionEditorKind.ChangeItemQuantity,
            cartItemId: currentItem.id,
            cartItem: currentItem,
          })
        }>
        <Icon name="edit" />
      </div>
    )
  }
  return (
    <>
      <div className="row">
        <div className="col-12">
          <Panel id="order-items" label="Order Contents" actions={actions}>
            <Grid>
              <HItems items={editOrderSvc.getCartItems()}>
                <Row separator={true}>
                  <Cell>
                    <OC.Item.WithDeleted><OC.Item.FullName /></OC.Item.WithDeleted> <AddItemModifierButton />
                  </Cell>
                  <Cell align="right">
                    <ChangeItemQuantityButton /> <OC.Item.WithDeleted><OC.Item.Quantity /></OC.Item.WithDeleted>
                  </Cell>
                  <Cell align="right">
                    <OC.Item.WithDeleted><OC.Data.Price /></OC.Item.WithDeleted>
                  </Cell>
                  <Cell align="right">
                    <OC.Item.WithDeleted><OC.Item.Total /></OC.Item.WithDeleted> <RemoveItemButton />
                  </Cell>
                </Row>
                <OC.ItemComments>
                  <Row>
                    <Cell>
                      <CellIndent level={1}>
                        {' '}
                        <OC.Item.WithDeleted><OC.Data.Comments /></OC.Item.WithDeleted>
                      </CellIndent>
                    </Cell>
                  </Row>
                </OC.ItemComments>
                <OC.Item.Modifiers>
                  <Row>
                    <Cell>
                      <CellIndent level={1}>
                        <OC.Item.WithDeleted><OC.Data.Name /></OC.Item.WithDeleted>:
                      </CellIndent>
                    </Cell>
                    <Cell></Cell>
                    <Cell></Cell>
                    <Cell></Cell>
                  </Row>
                  <OC.Item.Options>
                    <Row>
                      <Cell>
                        <CellIndent level={2}>
                          <OC.Item.WithDeleted><OC.Data.Name /></OC.Item.WithDeleted>{' '}
                        </CellIndent>
                      </Cell>
                      <Cell align="right">
                        <OC.Item.WithDeleted><OC.Item.Quantity /></OC.Item.WithDeleted>{' '}
                      </Cell>
                      <Cell>
                        <OC.Item.WithDeleted><OC.Data.Price /></OC.Item.WithDeleted>
                      </Cell>
                      <Cell align="right">
                        {' '}
                        <RemoveModifierOptionButton />
                      </Cell>
                    </Row>
                  </OC.Item.Options>
                </OC.Item.Modifiers>
              </HItems>
            </Grid>
          </Panel>
        </div>
      </div>
    </>
  )
}

function OrderTotals(props: { openEditor: (actionEditor: ActionEditor) => Promise<EditOrderAction> }): JSX.Element {
  const editOrderSvc = useEditOrderSvc()
  // const EditDiscountButton = (): JSX.Element => {
  //   return (
  //     <div
  //       className="badge badge-warning"
  //       style={{ cursor: 'pointer' }}
  //       title={'Edit Discount'}
  //       onClick={() => props.openEditor({ kind: ActionEditorKind.EditDiscount })}>
  //       <Icon name="edit" />
  //     </div>
  //   )
  // }

  return (
    <div className="row">
      <div className="col-12">
        <Panel id="totals" label="Totals">
          <Grid>
            <Row>
              <Cell>Subtotal:</Cell>
              <Cell align="right">
                <OC.NamedTotals.Subtotal />
              </Cell>
            </Row>
            <OC.NamedTotals.HasDiscount>
              <Row>
                <Cell>Discount:</Cell>
                <Cell align="right">
                  <OC.NamedTotals.Discount />
                </Cell>
              </Row>
            </OC.NamedTotals.HasDiscount>
            <OC.IsDelivery>
              <Row>
                <Cell>Delivery:</Cell>
                <Cell align="right">
                  <div
                    className="badge badge-warning"
                    style={{ cursor: 'pointer' }}
                    title={'Update delivery'}
                    onClick={() => props.openEditor({ kind: ActionEditorKind.EditDelivery })}>
                    <Icon name="edit" />
                  </div>{' '}
                  <OC.NamedTotals.Delivery />
                </Cell>
              </Row>
            </OC.IsDelivery>
            <Row>
              <Cell>
                Service:{' '}
                <div
                  className="badge badge-success"
                  style={{ cursor: 'pointer' }}
                  title={'Add New Service Charge'}
                  onClick={() => props.openEditor({ kind: ActionEditorKind.AddCharge })}>
                  <Icon name="plus" />
                  Add New Service Charge
                </div>
              </Cell>
              <Cell align="right">
                <OC.NamedTotals.Service />
              </Cell>
            </Row>
            <DetailedCharges key={`detailed-charges-${editOrderSvc.getChangesCount()}`} openEditor={props.openEditor} />
            {false && (
              <Row>
                <Cell>Service:</Cell>
                <Cell align="right">
                  <div
                    className="badge badge-warning"
                    style={{ cursor: 'pointer' }}
                    title={'Update service charges'}
                    onClick={() => props.openEditor({ kind: ActionEditorKind.EditService })}>
                    <Icon name="edit" />
                  </div>{' '}
                  <OC.NamedTotals.Service />
                </Cell>
              </Row>
            )}
            <Row>
              <Cell>Tax:</Cell>
              <Cell align="right">
                <OC.NamedTotals.Tax />
              </Cell>
            </Row>
            <Row>
              <Cell>Tip:</Cell>
              <Cell align="right">
                <div
                  className="badge badge-warning"
                  style={{ cursor: 'pointer' }}
                  title={'Update tip'}
                  onClick={() => props.openEditor({ kind: ActionEditorKind.EditTip })}>
                  <Icon name="edit" />
                </div>{' '}
                <OC.NamedTotals.Tip />
              </Cell>
            </Row>
            <Row>
              <Cell>Order Total:</Cell>
              <Cell align="right">
                <OC.NamedTotals.Total />
              </Cell>
            </Row>
          </Grid>
        </Panel>
      </div>
    </div>
  )
}


function ChangesSummary(): JSX.Element {
  const editOrderSvc = useEditOrderSvc()
  const actions = editOrderSvc.getChanges()
  const locale = editOrderSvc.getLocale()
  if (actions.length > 0 || editOrderSvc.hasRedo()) {
    return (
      <div className="row">
        <div className="col-12">
          <Panel
            id="totals"
            label="Upcoming order changes"
            actions={[
              editOrderSvc.hasRedo()
                ? { id: 'redo', style: 'unchecked', icon: 'redo', label: 'Redo Change', onClick: editOrderSvc.redo }
                : null,
              editOrderSvc.hasUndo()
                ? { id: 'undo', style: 'unchecked', icon: 'undo', label: 'Undo Change', onClick: editOrderSvc.undo }
                : null,
            ]}>
            <Grid>
              {actions.map(
                (a: EditOrderAction, index: number): JSX.Element => (
                  <Row key={index}>
                    <Cell>{renderActionSummary(a, locale)}</Cell>
                  </Row>
                )
              )}
            </Grid>
          </Panel>
        </div>
      </div>
    )
  } else {
    return <></>
  }
}

function renderActionSummary(a: EditOrderAction, locale: string): string {
  return getActionDescription(a, locale)
}

const createAddAction = (_api: EditOrderAPI, addAction: () => void): PanelAction => ({
  id: 'add-item',
  style: 'unchecked',
  icon: 'plus',
  label: 'Add Item',
  onClick: addAction,
})

export function EditOrderTab(): JSX.Element {
  return <EditOrder />
}
